import React, { Component } from 'react';
import { useAuth } from 'auth_context';
import { withRouter } from 'utilities';
import { ResourceWindow } from 'components';

class PublicSidebar extends Component {
	constructor(props) {
		super(props);
		this.state = { content_warnings_expanded: false };
	}

	render() {
		let categories = this.props.categories.map(cat => {
			return (
				<button
					onClick={() => this.props.select_category(cat)}
					className={`sidebar-category ${this.props.selected_categories.includes(cat) ? 'selected' : ''}`}
				>
					{cat}
				</button>
			);
		});

		// Straight alphabetical order — unlike the resource cards, selected tags
		// should stay in place here rather than jumping to the top.
		let sorted_tags = [...this.props.tags].sort((a, b) => a.localeCompare(b));

		let tags = sorted_tags.map(tag => {
			return (
				<button
					key={tag}
					onClick={() => this.props.select_tag(tag)}
					className={`sidebar-tag ${this.props.selected_tags.includes(tag) ? 'selected' : ''}`}
				>
					{tag}
				</button>
			);
		});

		// Alphabetical, but any "Other" catch-all sorts to the very bottom.
		const alpha_other_last = (a, b) => {
			const a_other = a.toLowerCase() === 'other';
			const b_other = b.toLowerCase() === 'other';
			if (a_other !== b_other) return a_other ? 1 : -1;
			return a.localeCompare(b);
		};

		let sorted_languages = [...this.props.languages].sort(alpha_other_last);
		let languages = sorted_languages.map(lang => (
			<button
				key={lang}
				onClick={() => this.props.select_language(lang)}
				className={`sidebar-tag ${this.props.selected_languages.includes(lang) ? 'selected' : ''}`}
			>
				{lang}
			</button>
		));

		let sorted_relevant_to = [...this.props.relevant_to].sort(alpha_other_last);
		let relevant_to = sorted_relevant_to.map(rel => (
			<button
				key={rel}
				onClick={() => this.props.select_relevant_to(rel)}
				className={`sidebar-tag ${this.props.selected_relevant_to.includes(rel) ? 'selected' : ''}`}
			>
				{rel}
			</button>
		));

		let sorted_content_warnings = [...this.props.content_warnings].sort((a, b) => a.localeCompare(b));
		// Selected warnings "pop up" into the Included area; the rest live in the
		// collapsible drawer below.
		let selected_cws = sorted_content_warnings.filter(cw =>
			this.props.selected_content_warnings.includes(cw)
		);
		let available_cws = sorted_content_warnings.filter(
			cw => !this.props.selected_content_warnings.includes(cw)
		);

		let included_cw_tags = selected_cws.map(cw => (
			<button
				key={cw}
				onClick={() => this.props.select_content_warning(cw)}
				className="sidebar-cw-tag sidebar-cw-tag--nsfw sidebar-cw-tag--included"
				title="Click to remove"
			>
				{cw}
			</button>
		));

		let available_cw_tags = available_cws.map(cw => (
			<button
				key={cw}
				onClick={() => this.props.select_content_warning(cw)}
				className="sidebar-cw-tag sidebar-cw-tag--nsfw"
				title="Click to include"
			>
				{cw}
			</button>
		));

		return (
			<div className="public-sidebar">
				<h3 className="sidebar-header">Search</h3>
				<input
					className="sidebar-search-bar"
					onChange={e => this.props.update_search_string(e.target.value)}
				/>

				<h3 className="sidebar-header">Resources</h3>
				{categories}
				<br />
				<br />
				<h3 className="sidebar-header">Tags</h3>
				{tags}

				<h3 className="sidebar-header">Language</h3>
				{languages}

				<h3 className="sidebar-header">Relevant To</h3>
				{relevant_to}

				<h3 className="sidebar-header">Content Warning</h3>
				<h5 className="sidebar-cw-label">Included:</h5>
				<div className="sidebar-cw-included">
					<span className="sidebar-cw-tag sidebar-cw-tag--sfw" title="Safe-for-work resources are always shown">
						SFW
					</span>
					{included_cw_tags}
				</div>

				<button
					className="sidebar-collapsible-toggle"
					onClick={() =>
						this.setState({ content_warnings_expanded: !this.state.content_warnings_expanded })
					}
				>
					<span
						className={`sidebar-collapsible-arrow ${this.state.content_warnings_expanded ? 'expanded' : ''}`}
					>
						&#9656;
					</span>
					More categories
				</button>
				{this.state.content_warnings_expanded && (
					<div className="sidebar-collapsible-inner">
						<h5 className="sidebar-collapsible-note">
							Select a category to include those resources.
						</h5>
						{available_cw_tags.length > 0 ? (
							available_cw_tags
						) : (
							<h5 className="sidebar-collapsible-note">All categories included.</h5>
						)}
					</div>
				)}
			</div>
		);
	}
}

class ResourcesPage extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,
			resources: [],
			categories: [],
			tags: [],
			languages: [],
			relevant_to: [],
			content_warnings: [],
			search_string: '',
			category_filters: [],
			tag_filters: [],
			language_filters: [],
			relevant_to_filters: [],
			content_warning_filters: [],
		};
	}

	componentDidMount() {
		// Pre-select filters passed via query params (e.g. /resources?tag=Housing
		// from a shared-resource page chip).
		const params = new URLSearchParams(window.location.search);
		const tag = params.get('tag');
		const category = params.get('category');
		if (tag || category) {
			this.setState({
				tag_filters: tag ? [tag] : [],
				category_filters: category ? [category] : [],
			});
		}

		this.fetch_resources();
	}

	fetch_resources = async () => {
		let url = '/api/get_resources/';

		let response = await this.props.auth.authFetch(url);

		const data = await response.json();

		this.setState({
			loading: false,
			resources: data.resources,
			categories: data.categories,
			tags: data.tags,
			languages: data.languages || [],
			relevant_to: data.relevant_to || [],
			content_warnings: data.content_warnings || [],
		});
	};

	resource_within_filters = r => {
		if (this.state.category_filters.length !== 0 && !this.state.category_filters.includes(r.category)) {
			return false;
		}

		if (this.state.tag_filters.length !== 0) {
			for (let tag of this.state.tag_filters) {
				if (!r.tags.includes(tag)) {
					return false;
				}
			}
		}

		if (this.state.language_filters.length !== 0) {
			for (let lang of this.state.language_filters) {
				if (!r.languages.includes(lang)) {
					return false;
				}
			}
		}

		if (this.state.relevant_to_filters.length !== 0) {
			for (let rel of this.state.relevant_to_filters) {
				if (!r.relevant_to.includes(rel)) {
					return false;
				}
			}
		}

		// Content warnings are opt-in: SFW (or unmarked) resources always show;
		// a specific warning category only appears once explicitly selected.
		if (
			r.content_warning &&
			r.content_warning !== 'SFW' &&
			!this.state.content_warning_filters.includes(r.content_warning)
		) {
			return false;
		}

		let search_tags = this.state.search_string.toUpperCase().split(' ');
		let resource_search_tags = r.search_field.toUpperCase().split(' ');

		let all_search_words_found = true;

		for (let tag of search_tags) {
			let found = false;
			for (let rs_tag of resource_search_tags) {
				if (rs_tag.includes(tag)) {
					found = true;
				}
			}
			if (!found) {
				all_search_words_found = false;
			}
		}

		return all_search_words_found;
	};

	select_category = cat => {
		this.setState({ loading: true });

		if (this.state.category_filters.includes(cat)) {
			this.setState(
				{
					category_filters: this.state.category_filters.filter(c => {
						return c !== cat;
					}),
				},
				() => this.setState({ loading: false })
			);
		} else {
			let cat_filters = [...this.state.category_filters, cat];
			this.setState(
				{
					category_filters: [...this.state.category_filters, cat],
				},
				() => this.setState({ loading: false })
			);
		}
	};

	select_tag = tag => {
		this.setState({ loading: true });

		if (this.state.tag_filters.includes(tag)) {
			this.setState(
				{
					tag_filters: this.state.tag_filters.filter(t => {
						return t !== tag;
					}),
				},
				() => this.setState({ loading: false })
			);
		} else {
			this.setState(
				{
					tag_filters: [...this.state.tag_filters, tag],
				},
				() => this.setState({ loading: false })
			);
		}
	};

	// Shared toggle for the simpler array-filter fields (language, relevant-to,
	// content-warning) — same include/exclude behavior as select_tag above.
	toggle_filter = (key, value) => {
		this.setState({ loading: true });

		const current = this.state[key];
		const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];

		this.setState({ [key]: next }, () => this.setState({ loading: false }));
	};

	select_language = lang => this.toggle_filter('language_filters', lang);
	select_relevant_to = rel => this.toggle_filter('relevant_to_filters', rel);
	select_content_warning = cw => this.toggle_filter('content_warning_filters', cw);

	render() {
		let filtered_resources = this.state.resources.filter(this.resource_within_filters);

		return (
			<div>
				<PublicSidebar
					categories={this.state.categories}
					tags={this.state.tags}
					languages={this.state.languages}
					relevant_to={this.state.relevant_to}
					content_warnings={this.state.content_warnings}
					update_search_string={s => this.setState({ search_string: s })}
					select_category={this.select_category}
					select_tag={this.select_tag}
					select_language={this.select_language}
					select_relevant_to={this.select_relevant_to}
					select_content_warning={this.select_content_warning}
					selected_categories={this.state.category_filters}
					selected_tags={this.state.tag_filters}
					selected_languages={this.state.language_filters}
					selected_relevant_to={this.state.relevant_to_filters}
					selected_content_warnings={this.state.content_warning_filters}
				/>

				<ResourceWindow
					resources={filtered_resources}
					loading={this.state.loading}
					select_category={this.select_category}
					select_tag={this.select_tag}
					selected_tags={this.state.tag_filters}
					user={this.props.auth.user}
				/>
			</div>
		);
	}
}

const Resources = props => {
	const auth = useAuth();
	return <ResourcesPage {...props} auth={auth} />;
};

export default withRouter(Resources);
